import React from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { warningTriangleIcon } from "@progress/kendo-svg-icons";
import type { RawFileJobUsage, StorageDeletionPreview } from "@domain/schemas/BI";

interface DeleteFileDialogProperties {
  fileName: string;
  folder: string;
  /**
  `.d` acquisitions are directory bundles of many objects rather than a single file.
  */
  isDirectoryBundle: boolean;
  /**
   * Ordinary folders require a complete recursive deletion preview.
   */
  isFolder: boolean;
  /**
   * Jobs still referencing the file. Empty means no job depends on it, in which case no warning is shown.
   * `undefined` means the usage lookup has not resolved yet. Only current-project jobs are returned.
   */
  usedByJobs?: RawFileJobUsage[] | null;
  canDelete: boolean;
  requiresWarning: boolean;
  isLoadingUsage: boolean;
  isDeleting: boolean;
  usageLookupFailed: boolean;
  error?: string | null;
  files?: StorageDeletionPreview["files"];
  onConfirm: () => void;
  onCancel: () => void;
}

const MAX_LISTED_JOBS = 10;

export const DeleteFileDialog: React.FC<DeleteFileDialogProperties> = ({
  fileName,
  folder,
  isDirectoryBundle,
  isFolder,
  usedByJobs,
  canDelete,
  requiresWarning,
  isLoadingUsage,
  isDeleting,
  usageLookupFailed,
  error,
  files,
  onConfirm,
  onCancel,
}) => {
  const jobCount = usedByJobs?.length ?? 0;
  const isInUse = requiresWarning;
  const listedJobs = usedByJobs?.slice(0, MAX_LISTED_JOBS) ?? [];
  const hiddenJobCount = jobCount - listedJobs.length;

  let cascadeNotice = "";
  if (isFolder) {
    cascadeNotice = " All listed files will be removed, including other file types and nested folders.";
  } else if (isDirectoryBundle) {
    cascadeNotice = " Every file in this acquisition folder will be removed.";
  }

  let confirmLabel = "Delete";
  if (isDeleting) {
    confirmLabel = "Deleting…";
  } else if (isFolder || isInUse) {
    confirmLabel = "Delete anyway";
  }

  return (
    <Dialog title={isFolder ? "Delete folder" : "Delete file"} onClose={isDeleting ? undefined : onCancel} width={620} style={{ maxHeight: "90vh" }}>
      <div style={{ padding: "10px" }}>
        <p style={{ marginTop: 0 }}>
          Permanently delete <strong>{fileName}</strong> from <strong>{folder || "Root"}</strong>?{cascadeNotice} This cannot be undone.
        </p>

        {isLoadingUsage && (
          <p role="status" style={{ fontSize: "0.8rem", color: "#666" }}>
            {isFolder ? "Loading all files and checking job usage…" : "Checking whether any jobs use this file…"}
          </p>
        )}

        {error && (
          <p role="alert" style={{ color: "var(--kendo-color-error, #b91c1c)", whiteSpace: "pre-line" }}>
            {error}
          </p>
        )}
        {isFolder && files && (
          <div>
            <p>
              <strong>{files.length}</strong> {files.length === 1 ? "file" : "files"} will be permanently deleted.
            </p>
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Keyboard users need to focus the scroll container. */}
            <div role="region" aria-label="Folder deletion preview" tabIndex={0} style={{ maxHeight: "min(240px, 30vh)", overflowY: "auto", overflowWrap: "anywhere" }}>
              <ul aria-label="Files to delete">
                {files.map((file) => (
                  <li key={file.path}>
                    {file.path} ({file.sizeInBytes} bytes)
                  </li>
                ))}
              </ul>
            </div>
            {files.length === 0 && <p>This folder contains no stored objects.</p>}
          </div>
        )}

        {!isLoadingUsage && !usageLookupFailed && !canDelete && (
          <p role="alert">Deletion is blocked because running or queued jobs still use this target. Jobs in other projects may also prevent deletion.</p>
        )}

        {!isLoadingUsage && canDelete && isInUse && (
          <div role="alert" style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "12px", color: "#b26a00" }}>
            <SvgIcon icon={warningTriangleIcon} />
            <span>
              {isFolder ? "Files in this folder are" : "This file is"} referenced by jobs that may run again. Deleting {isFolder ? "them" : "it"} can break rerunning or reconfiguring those jobs.
              References in other projects may also apply.
            </span>
          </div>
        )}

        {!isLoadingUsage && jobCount > 0 && (
          <div style={{ maxHeight: "min(200px, 20vh)", overflowY: "auto", border: "1px solid #eee", borderRadius: "4px", padding: "8px" }}>
            <ul aria-label={isFolder ? "Jobs using files in this folder" : "Jobs using this file"} style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {listedJobs.map((job, index) => (
                <li key={job.jobId ?? `${job.jobName ?? "job"}-${index}`} style={{ padding: "4px 0", borderBottom: "1px solid #f5f5f5", fontSize: "0.85rem" }}>
                  <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={job.jobName ?? undefined}>
                    {job.jobName ?? "Unnamed job"}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#666" }}>Created by {job.createdBy ?? "Unknown user"}</div>
                </li>
              ))}
            </ul>
            {hiddenJobCount > 0 && <p style={{ fontSize: "0.75rem", color: "#666", margin: "8px 0 0" }}>and {hiddenJobCount} more…</p>}
          </div>
        )}

        {!isLoadingUsage && usageLookupFailed && (
          <div role="alert" style={{ display: "flex", alignItems: "flex-start", gap: "8px", color: "#b26a00" }}>
            <SvgIcon icon={warningTriangleIcon} />
            <span>
              {isFolder ? "Could not load the folder deletion preview. No files have been deleted. Cancel and try again." : "Could not check whether jobs use this file. It may still be referenced."}
            </span>
          </div>
        )}
      </div>

      <DialogActionsBar>
        <Button onClick={onCancel} disabled={isDeleting}>
          Cancel
        </Button>
        <Button themeColor="error" onClick={onConfirm} disabled={isDeleting || isLoadingUsage || usageLookupFailed || !canDelete || (isFolder && !files)}>
          {confirmLabel}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};
